// src/components/shared/SmartAvatar.tsx
import * as React from 'react'

import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

type Status = 'online' | 'offline' | 'busy' | 'away'
type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const SIZE_CLASS: Record<Size, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-16 w-16 text-base',
  xl: 'h-20 w-20 text-lg',
  '2xl': 'h-24 w-24 text-xl',
}

const BORDER_SIZE_CLASS: Record<Size, string> = {
  xs: 'border',
  sm: 'border',
  md: 'border-2',
  lg: 'border-[3px]',
  xl: 'border-[3px]',
  '2xl': 'border-[4px]',
}

const STATUS_CLASS: Record<Status, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-gray-300',
  busy: 'bg-red-500',
  away: 'bg-yellow-400',
}

const loadedImages = new Set<string>()

function useInView<T extends HTMLElement>(enabled = true) {
  const ref = React.useRef<T | null>(null)
  const [inView, setInView] = React.useState(!enabled)

  React.useEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { rootMargin: '120px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled])

  return { ref, inView }
}

export interface SmartAvatarProps extends React.ComponentPropsWithoutRef<typeof Avatar> {
  src?: string
  alt?: string
  name?: string
  size?: Size
  rounded?: 'full' | 'lg' | 'md'
  ring?: boolean
  status?: Status
  enableStatus?: boolean
  className?: string
  imgClassName?: string
  eager?: boolean
  blurDataURL?: string
  srcSet?: string
  sizes?: string
}

export const SmartAvatar = React.forwardRef<HTMLSpanElement, SmartAvatarProps>(
  (
    {
      src,
      alt,
      name,
      size = 'xl',
      rounded = 'full',
      ring = true,
      status,
      enableStatus = false,
      className,
      imgClassName,
      eager = false,
      blurDataURL,
      srcSet,
      sizes,
      ...rest
    },
    ref
  ) => {
    const { i18n } = useTranslation()
    const isArabic = i18n.language?.startsWith('ar')

    const getLocalizedInitials = React.useCallback(
      (input?: string) => {
        if (!input) return ''
        const words = input.trim().split(/\s+/)
        if (isArabic) {
          // Arabic initials: use only the first visible character of the first word
          return words[0]?.[0] ?? ''
        }
        // English / Latin initials: first letters of up to 2 words
        const [a, b] = words
        return (a?.[0] ?? '').concat(b?.[0] ?? '').toUpperCase()
      },
      [isArabic]
    )

    const { ref: inViewRef, inView } = useInView<HTMLSpanElement>(!eager)
    const hostRef = React.useRef<HTMLSpanElement | null>(null)

    React.useImperativeHandle(ref, () => hostRef.current as HTMLSpanElement)
    const setRefs = React.useCallback(
      (node: HTMLSpanElement | null) => {
        hostRef.current = node
        ;(inViewRef as unknown as { current: HTMLSpanElement | null }).current = node
      },
      [inViewRef]
    )

    const [loaded, setLoaded] = React.useState(() => (src ? loadedImages.has(src) : false))
    const [failed, setFailed] = React.useState(false)

    React.useEffect(() => {
      if (!src || failed || loaded) return
      if (!inView && !eager) return
      const img = new Image()
      img.decoding = 'async'
      img.loading = eager ? 'eager' : 'lazy'
      img.src = src
      if (srcSet) img.srcset = srcSet
      if (sizes) img.sizes = sizes
      img.onload = () => {
        loadedImages.add(src)
        setLoaded(true)
      }
      img.onerror = () => setFailed(true)
    }, [src, srcSet, sizes, inView, eager, loaded, failed])

    const showImage = !!src && !failed && (loaded || inView || eager)
    const initials = getLocalizedInitials(name || alt)

    return (
      <div className={cn('relative inline-flex', SIZE_CLASS[size])} dir={isArabic ? 'rtl' : 'ltr'}>
        {blurDataURL && !loaded && !failed && (
          <img
            src={blurDataURL}
            alt=''
            aria-hidden
            className={cn(
              'absolute inset-0 h-full w-full scale-105 rounded-full object-cover opacity-80 blur-md transition-opacity duration-300'
            )}
          />
        )}

        <Avatar
          ref={setRefs}
          className={cn(
            SIZE_CLASS[size],
            rounded === 'full' && 'rounded-full',
            rounded === 'lg' && 'rounded-lg',
            rounded === 'md' && 'rounded-md',
            ring && cn(BORDER_SIZE_CLASS[size], 'border-background'),
            className
          )}
          {...rest}
        >
          {showImage ? (
            <AvatarImage
              src={src}
              alt={alt || name || 'avatar'}
              loading={eager ? 'eager' : 'lazy'}
              className={cn(
                loaded ? 'blur-0 scale-100 opacity-100' : 'scale-105 opacity-0 blur-sm',
                'transition-all duration-500 ease-out',
                imgClassName
              )}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />
          ) : (
            <AvatarFallback className={cn('flex items-center justify-center bg-gray-200/70 font-medium text-gray-500')}>
              {initials || <span className='inline-block h-1 w-1 rounded-full bg-gray-500/80 align-middle' />}
            </AvatarFallback>
          )}
        </Avatar>

        {enableStatus && status && (
          <span
            className={cn('ring-background absolute top-0 left-0 h-3 w-3 rounded-full ring-2', STATUS_CLASS[status])}
          />
        )}
      </div>
    )
  }
)

SmartAvatar.displayName = 'SmartAvatar'
